<script setup lang="ts">
import IconCheckmark from "@dsk/icons/LeCheckmark.vue";
import IconCross from "@dsk/icons/LeCross.vue";

withDefaults(
  defineProps<{
    modelValue?: boolean;
  }>(),
  { modelValue: false }
);

const emit = defineEmits<{
  (event: "update:modelValue", value: boolean): void;
}>();
</script>

<template>
  <button
    type="button"
    role="switch"
    :class="['tg-toggle', modelValue ? 'tg-toggle--checked' : '']"
    :aria-checked="modelValue"
    aria-label="Toggle"
    @click="emit('update:modelValue', !modelValue)"
  >
    <IconCheckmark
      role="presentation"
      class="tg-toggle__icon tg-toggle__icon-check"
      :class="{ 'tg-toggle__icon--visible': modelValue }"
    />
    <IconCross class="tg-toggle__icon tg-toggle__icon-cross" :class="{ 'tg-toggle__icon--visible': !modelValue }" />
    <span class="tg-toggle__knob"></span>
  </button>
</template>

<style lang="less">
@import "@dsk/styles/all";

.tg-toggle {
  display: inline-flex;
  position: relative;
  width: 46px;
  height: 25px;
  padding: 0;
  overflow: hidden;
  transition: all 100ms ease-in-out;
  border: 2px solid @colors[@secondary][500];
  border-radius: 12.5px;
  background-color: @colors[@secondary][500];
  vertical-align: middle;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &:focus,
  &:hover:not(:disabled) {
    outline: none;
    box-shadow: 0 0 0 4px fade(@colors[@secondary][500], 40%);
  }

  &--checked {
    border-color: @colors[@primary][500];
    background-color: @colors[@primary][500];

    &:focus,
    &:hover:not(:disabled) {
      box-shadow: 0 0 0 4px fade(@colors[@primary][400], 30%);
    }
  }

  &__knob {
    position: relative;
    left: 0;
    width: 21px;
    height: 21px;
    transition: 0.4s;
    border-radius: 50%;
    background: @colors[@neutrals][100];
    box-shadow: 0 2px 4px 0 #2b70c01a;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }

  &--checked .tg-toggle__knob {
    left: 21px;
  }

  &__icon {
    display: none;
    position: absolute;
    top: 0;
    bottom: 0;
    width: 12px;
    height: 12px;
    margin: auto 0;
    color: @colors[@secondary][600];

    &--visible {
      display: inline-flex;
    }
  }

  &__icon-check {
    top: 0;
    left: 5px;
    color: @colors[@primary][800];
  }

  &__icon-cross {
    top: 0;
    right: 6.5px;
    width: 10px;
    height: 10px;
  }
}
</style>
